import React, { useState, useEffect } from 'react';
import { sendRequest } from '../../api';
import { useUserContext } from '../UserProvider';
import TopicList from './TopicList';
import TopicForm from './TopicForm';
import TopicDetail from './TopicDetail';
import ConversationalTopicCreator from './ConversationalTopicCreator';
import { ThemeProvider, ThemeToggle } from '../ThemeToggle';

const Topics = () => {
  const [topics, setTopics] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [useConversationalCreator, setUseConversationalCreator] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingTopic, setEditingTopic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newlyCreatedTopicId, setNewlyCreatedTopicId] = useState(null);
  const { userData } = useUserContext();

  // Fetch user's topics
  const fetchTopics = async () => {
    if (!userData?.id) return;
    
    try {
      setLoading(true);
      const response = await sendRequest(`/topics?user_id=${userData.id}`, 'GET');
      setTopics(response);
      setError(null);
      
      // Auto-show conversational creator for new users
      if (response.length === 0 && !isCreating && !useConversationalCreator) {
        setUseConversationalCreator(true);
      }
    } catch (err) {
      setError('Failed to load topics');
      console.error('Error fetching topics:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userData?.id) {
      fetchTopics();
    }
  }, [userData?.id]);

  const handleCreateTopic = async (topicData) => {
    try {
      const response = await sendRequest('/topics', 'POST', {
        ...topicData,
        user_id: userData.id
      });
      setTopics([response, ...topics]);
      setIsCreating(false);
      setUseConversationalCreator(false);
      setSelectedTopic(response);
      setNewlyCreatedTopicId(response.id);
      return response;
    } catch (err) {
      throw err;
    }
  };

  const handleSelectTopic = (topic) => {
    setSelectedTopic(topic);
    setIsCreating(false);
    setUseConversationalCreator(false);
    if (newlyCreatedTopicId !== topic.id) {
      setNewlyCreatedTopicId(null);
    }
  };

  const handleBack = () => {
    setSelectedTopic(null);
    fetchTopics(); // Refresh topics list
  };

  const handleDeleteTopic = async (topicId) => {
    if (!window.confirm('Are you sure you want to delete this topic? This action cannot be undone.')) return;
    try {
      await sendRequest(`/topics/${topicId}`, 'DELETE');
      const updatedTopics = topics.filter(t => t.id !== topicId);
      setTopics(updatedTopics);
      if (selectedTopic && selectedTopic.id === topicId) {
        setSelectedTopic(null);
      }
      
      // Show conversational creator if this was the last topic
      if (updatedTopics.length === 0) {
        setUseConversationalCreator(true);
      }
    } catch (err) {
      setError('Failed to delete topic');
      console.error('Error deleting topic:', err);
    }
  };

  const handleCreateNew = () => {
    // Always use conversational creator for new topics
    setUseConversationalCreator(true);
    setIsCreating(false);
  };

  const handleEditTopic = (topic) => {
    setEditingTopic(topic);
    setIsEditing(true);
    setIsCreating(false);
    setUseConversationalCreator(false);
  };

  const handleUpdateTopic = async (updatedData) => {
    try {
      const response = await sendRequest(`/topics/${editingTopic.id}`, 'PUT', {
        ...updatedData,
        user_id: userData.id
      });
      // Update topics list
      setTopics(topics.map(t => t.id === editingTopic.id ? response : t));
      setSelectedTopic(response);
      setIsEditing(false);
      setEditingTopic(null);
      return response;
    } catch (err) {
      throw err;
    }
  };

  const handleCancelConversationalCreator = () => {
    setUseConversationalCreator(false);
    // If they have no topics and cancel, show them the traditional form
    if (topics.length === 0) {
      setIsCreating(true);
    }
  };

  if (!userData) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-gray-500">Please log in to manage topics</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <ThemeProvider>
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Conversational Topic Creator */}
          {useConversationalCreator && (
            <ConversationalTopicCreator
              onCreateTopic={handleCreateTopic}
              onCancel={handleCancelConversationalCreator}
            />
          )}

          {isEditing ? (
            <div>
              <button
                onClick={() => { setIsEditing(false); setEditingTopic(null); }}
                className="mb-4 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
              >
                ← Back to topic
              </button>
              <TopicForm
                onSubmit={handleUpdateTopic}
                onCancel={() => { setIsEditing(false); setEditingTopic(null); }}
                initialData={editingTopic}
              />
            </div>
          ) : selectedTopic ? (
            <TopicDetail
              topic={selectedTopic}
              onBack={handleBack}
              onDeleteTopic={handleDeleteTopic}
              isNewlyCreated={selectedTopic.id === newlyCreatedTopicId}
              onEditTopic={handleEditTopic}
            />
          ) : isCreating ? (
            <div>
              <button
                onClick={() => setIsCreating(false)}
                className="mb-4 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
              >
                ← Back to topics
              </button>
              <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <p className="text-blue-800 dark:text-blue-200 text-sm">
                  💡 Want a faster way? Try our <button 
                    onClick={() => { setIsCreating(false); setUseConversationalCreator(true); }}
                    className="underline font-medium hover:text-blue-900 dark:hover:text-blue-100"
                  >
                    conversational setup
                  </button> instead!
                </p>
              </div>
              <TopicForm onSubmit={handleCreateTopic} onCancel={() => setIsCreating(false)} />
            </div>
          ) : (
            <div>
              <div className="mb-8 flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                    Your Intelligence Agents
                  </h1>
                  <p className="text-gray-600 dark:text-gray-400">
                    Track anything on the internet with AI agents that learn what matters to you
                  </p>
                </div>
                <ThemeToggle className="ml-4" />
              </div>

              {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-red-600">{error}</p>
                </div>
              )}

              <TopicList
                topics={topics}
                onSelectTopic={handleSelectTopic}
                onCreateNew={handleCreateNew}
                onDeleteTopic={handleDeleteTopic}
              />
            </div>
          )}
        </div>
      </div>
    </ThemeProvider>
  );
};

export default Topics; 